import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Plus, Edit2, Trash2, Eye, EyeOff, GripVertical, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const FormFieldsManager = () => {
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [formData, setFormData] = useState({
    fieldName: '',
    fieldLabel: '',
    fieldType: 'text',
    placeholder: '',
    isRequired: false,
    displayOrder: 0,
    options: [],
  });

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'email', label: 'Email' },
    { value: 'tel', label: 'Phone' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'select', label: 'Dropdown' },
    { value: 'checkbox', label: 'Checkbox' },
  ];

  useEffect(() => {
    loadFields();
  }, []);

  const loadFields = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/form-fields?includeInactive=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFields(response.data);
    } catch (error) {
      toast.error('Failed to load form fields');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (field = null) => {
    if (field) {
      setEditingField(field);
      setFormData({
        fieldName: field.field_name,
        fieldLabel: field.field_label,
        fieldType: field.field_type,
        placeholder: field.placeholder || '',
        isRequired: field.is_required,
        displayOrder: field.display_order,
        options: field.options || [],
      });
    } else {
      setEditingField(null);
      setFormData({
        fieldName: '',
        fieldLabel: '',
        fieldType: 'text',
        placeholder: '',
        isRequired: false,
        displayOrder: fields.length,
        options: [],
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = sessionStorage.getItem('token');

    try {
      if (editingField) {
        await axios.put(`${API_URL}/form-fields/${editingField.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Field updated successfully');
      } else {
        await axios.post(`${API_URL}/form-fields`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Field created successfully');
      }
      loadFields();
      setShowModal(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save field');
    }
  };

  const handleToggle = async (fieldId) => {
    const token = sessionStorage.getItem('token');
    try {
      await axios.patch(`${API_URL}/form-fields/${fieldId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Field status updated');
      loadFields();
    } catch (error) {
      toast.error('Failed to update field status');
    }
  };

  const handleToggleRequired = async (fieldId) => {
    const token = sessionStorage.getItem('token');
    try {
      await axios.patch(`${API_URL}/form-fields/${fieldId}/toggle-required`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Field requirement updated');
      loadFields();
    } catch (error) {
      toast.error('Failed to update field requirement');
    }
  };

  const handleDelete = async (fieldId) => {
    if (!window.confirm('Are you sure you want to delete this field?')) return;

    const token = sessionStorage.getItem('token');
    try {
      await axios.delete(`${API_URL}/form-fields/${fieldId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Field deleted successfully');
      loadFields();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete field');
    }
  };

  const handleAddOption = () => {
    setFormData({
      ...formData,
      options: [...formData.options, '']
    });
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  const handleRemoveOption = (index) => {
    const newOptions = formData.options.filter((_, i) => i !== index);
    setFormData({ ...formData, options: newOptions });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin?tab=settings')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to Settings"
            >
              <ArrowLeft size={24} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Client Form Fields</h1>
              <p className="text-gray-600 mt-1">Customize registration and profile forms</p>
            </div>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Plus size={20} />
            <span>Add Field</span>
          </button>
        </div>

        <div className="card">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Field Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Label
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Required
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {fields.map((field) => (
                  <tr key={field.id} className={!field.is_active ? 'bg-gray-50 opacity-60' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-gray-400">
                        <GripVertical size={16} />
                        <span className="ml-2 text-sm text-gray-900">{field.display_order}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded">{field.field_name}</code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{field.field_label}</div>
                      {field.placeholder && (
                        <div className="text-xs text-gray-500">Placeholder: {field.placeholder}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                        {field.field_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleRequired(field.id)}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                          field.is_required
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                        title={`Click to make ${field.is_required ? 'optional' : 'required'}`}
                      >
                        {field.is_required ? 'Required' : 'Optional'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggle(field.id)}
                        className={`flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium ${
                          field.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {field.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
                        <span>{field.is_active ? 'Active' : 'Inactive'}</span>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleOpenModal(field)}
                        className="text-primary-600 hover:text-primary-900 mr-3"
                        title="Edit field"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(field.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete field"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
              <h2 className="text-2xl font-bold mb-6">
                {editingField ? 'Edit Field' : 'Add New Field'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Field Name (code)</label>
                    <input
                      type="text"
                      className="input"
                      value={formData.fieldName}
                      onChange={(e) => setFormData({ ...formData, fieldName: e.target.value })}
                      disabled={!!editingField}
                      required
                      placeholder="e.g., address, company_name"
                    />
                    <p className="text-xs text-gray-500 mt-1">Lowercase, underscores only</p>
                  </div>

                  <div>
                    <label className="label">Display Label</label>
                    <input
                      type="text"
                      className="input"
                      value={formData.fieldLabel}
                      onChange={(e) => setFormData({ ...formData, fieldLabel: e.target.value })}
                      required
                      placeholder="e.g., Street Address"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Field Type</label>
                    <select
                      className="input"
                      value={formData.fieldType}
                      onChange={(e) => setFormData({ ...formData, fieldType: e.target.value })}
                      required
                    >
                      {fieldTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="label">Display Order</label>
                    <input
                      type="number"
                      className="input"
                      value={formData.displayOrder}
                      onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) })}
                      min="0"
                    />
                  </div>
                </div>

                <div>
                  <label className="label">Placeholder Text</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.placeholder}
                    onChange={(e) => setFormData({ ...formData, placeholder: e.target.value })}
                    placeholder="Optional hint text"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="isRequired"
                    checked={formData.isRequired}
                    onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                    className="rounded"
                  />
                  <label htmlFor="isRequired" className="text-sm font-medium">
                    Required field
                  </label>
                </div>

                {(formData.fieldType === 'select' || formData.fieldType === 'checkbox') && (
                  <div>
                    <label className="label">
                      {formData.fieldType === 'select' ? 'Dropdown Options' : 'Checkbox Options'}
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      {formData.fieldType === 'checkbox' 
                        ? 'Users can select multiple options' 
                        : 'Users can select one option'}
                    </p>
                    <div className="space-y-2">
                      {formData.options.map((option, index) => (
                        <div key={index} className="flex space-x-2">
                          <input
                            type="text"
                            className="input flex-1"
                            value={option}
                            onChange={(e) => handleOptionChange(index, e.target.value)}
                            placeholder={`Option ${index + 1}`}
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveOption(index)}
                            className="btn btn-secondary"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={handleAddOption}
                        className="btn btn-secondary w-full flex items-center justify-center gap-2"
                      >
                        <Plus size={16} />
                        <span>Add Option</span>
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="flex-1 btn btn-primary">
                    {editingField ? 'Update Field' : 'Create Field'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormFieldsManager;
